import * as React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Box, Typography, Tooltip } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Add from "@mui/icons-material/Add";
import { InfoOutlined, TipsAndUpdatesOutlined } from "@mui/icons-material";
import { Paper, Divider } from "@mui/material";

type ParamOption = { label: string; value: string; type?: number; status?: boolean };

export type Parameter = {
  code: string;
  label: string;
  type: number; // 1 number int, 2 select, 3 float
  options?: ParamOption[];
};

type NumOp = "eq" | "gte" | "lte" | "between";
type SelOp = "eq" | "in";

export type FilterRow =
  | { id: string; code: string; label: string; type: 1 | 3; op: NumOp; v1: number | null; v2: number | null }
  | { id: string; code: string; label: string; type: 2; op: SelOp; v1: string | string[] | null };

export type GroupLogic = "AND" | "OR";

export type FilterGroup = {
  id: string;
  logic: GroupLogic; // logic inside the group (parentheses)
  rows: FilterRow[];
};

export type ParamFilterState = {
  logic: GroupLogic; // logic between groups
  groups: FilterGroup[];
};

const selectOps = [
  { key: "eq", label: "=" },
  { key: "in", label: "in" },
];

const numOps = [
  { key: "eq", label: "=" },
  { key: "gte", label: "≥" },
  { key: "lte", label: "≤" },
  { key: "between", label: "between" },
];

export const newId = () =>
  typeof crypto !== "undefined" && (crypto as any).randomUUID
    ? (crypto as any).randomUUID()
    : String(Date.now() + Math.random());

const newEmptyRow = (): FilterRow =>
  ({ id: newId(), code: "", label: "", type: 1, op: "eq", v1: null, v2: null } as FilterRow);

const newGroup = (logic: GroupLogic = "AND"): FilterGroup => ({
  id: newId(),
  logic,
  rows: [newEmptyRow()],
});

export const rowIsComplete = (row: FilterRow) => {
  if (!row.code) return false;
  if (row.type === 2) {
    if (row.op === "eq") return row.v1 !== null && row.v1 !== "";
    return Array.isArray(row.v1) && row.v1.length > 0;
  }
  if (row.op === "between") return row.v1 !== null && row.v2 !== null;
  return row.v1 !== null;
};

const optionLabel = (param: Parameter | undefined, value: string) => {
  const opt = param?.options?.find((o) => String(o.value) === String(value));
  return opt?.label ?? value;
};

const optionLabels = (param: Parameter | undefined, values: string[]) => values.map((v) => optionLabel(param, v));

const summarizeRow = (row: FilterRow, getParamByCode: (code: string) => Parameter | undefined) => {
  const param = getParamByCode(row.code);

  const opLabel =
    row.type === 2
      ? row.op === "eq"
        ? "="
        : "in"
      : row.op === "eq"
        ? "="
        : row.op === "gte"
          ? ">="
          : row.op === "lte"
            ? "<="
            : "between";

  const val =
    row.type === 2
      ? row.op === "eq"
        ? row.v1
          ? optionLabel(param, String(row.v1))
          : ""
        : Array.isArray(row.v1)
          ? optionLabels(param, row.v1.map(String)).join(", ")
          : ""
      : row.op === "between"
        ? `${row.v1 ?? ""} .. ${row.v2 ?? ""}`
        : String(row.v1 ?? "");

  return `${row.label} ${opLabel} ${val}`.trim();
};

export function ParamFilterDialog({
  open,
  onClose,
  params, // flattened parameters from API
  value, // current filter state
  onApply,
  onReset,
}: {
  open: boolean;
  onClose: () => void;
  params: Parameter[];
  value: ParamFilterState;
  onApply: (next: ParamFilterState) => void;
  onReset: () => void;
}) {
  const [draft, setDraft] = React.useState<ParamFilterState>(
    value?.groups?.length ? value : { logic: "AND", groups: [newGroup("AND")] }
  );

  React.useEffect(() => {
    if (!open) return;
    setDraft(value?.groups?.length ? value : { logic: "AND", groups: [newGroup("AND")] });
  }, [open, value]);

  const getParamByCode = (code: string) => params.find((p) => p.code === code);

  const updateTopLogic = (logic: GroupLogic) => setDraft((s) => ({ ...s, logic }));

  const addGroup = () => {
    setDraft((s) => ({ ...s, groups: [...s.groups, newGroup("OR")] }));
  };

  const removeGroup = (groupId: string) => {
    setDraft((s) => ({ ...s, groups: s.groups.filter((g) => g.id !== groupId) }));
  };

  const updateGroupLogic = (groupId: string, logic: GroupLogic) => {
    setDraft((s) => ({
      ...s,
      groups: s.groups.map((g) => (g.id === groupId ? { ...g, logic } : g)),
    }));
  };

  const addRowToGroup = (groupId: string) => {
    setDraft((s) => ({
      ...s,
      groups: s.groups.map((g) => (g.id === groupId ? { ...g, rows: [...g.rows, newEmptyRow()] } : g)),
    }));
  };

  const removeRow = (groupId: string, rowId: string) => {
    setDraft((s) => ({
      ...s,
      groups: s.groups.map((g) => {
        if (g.id !== groupId) return g;
        const nextRows = g.rows.filter((r) => r.id !== rowId);
        return { ...g, rows: nextRows.length ? nextRows : [newEmptyRow()] }; // keep at least 1 row
      }),
    }));
  };

  const updateRow = (groupId: string, rowId: string, patch: Partial<FilterRow>) => {
    setDraft((s) => ({
      ...s,
      groups: s.groups.map((g) =>
        g.id === groupId
          ? { ...g, rows: g.rows.map((r) => (r.id === rowId ? ({ ...r, ...patch } as FilterRow) : r)) }
          : g
      ),
    }));
  };

  const resetAll = () => {
    setDraft({ logic: "AND", groups: [newGroup("AND")] });
    onReset();
  };

  const allRows = React.useMemo(() => draft.groups.flatMap((g) => g.rows), [draft.groups]);

  const anyComplete = allRows.some(rowIsComplete);
  const noHalfFilled = allRows.filter((r) => r.code).every(rowIsComplete);
  const canApply = anyComplete && noHalfFilled;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span>Filter by input parameters</span>
            <Tooltip
              arrow
              title={
                <div>
                  <Typography variant="body2">
                     Add conditions inside a group.
                  </Typography>
                  <Typography variant="body2">
                     Use "Within group" to choose AND / OR logic.
                  </Typography>
                  <Typography variant="body2">
                     Add multiple groups to create parentheses logic.
                  </Typography>
                  <Typography variant="body2">
                    Example:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    Sleeper = A AND (Fastening = B OR Bridge = Wood)
                  </Typography>
                </div>
              }
            >
              <InfoOutlined color="info" sx={{ cursor: 'pointer' }} />
            </Tooltip>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        {/* Top controls */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1}
          m={2}
        >
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <InputLabel>Combine groups</InputLabel>
            <Select
              label="Combine groups"
              value={draft.logic}
              onChange={(e) => updateTopLogic(e.target.value as GroupLogic)}
            >
              <MenuItem value="AND">AND</MenuItem>
              <MenuItem value="OR">OR</MenuItem>
            </Select>
          </FormControl>

          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={addGroup}
            sx={{
              textTransform: "none",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Add group
          </Button>
        </Box>
        <FilterExpressionPreview
          filter={draft}
          getParamByCode={getParamByCode}
          summarizeRow={summarizeRow}
          rowIsComplete={rowIsComplete}
        />

        {/* Groups */}
        {draft.groups.map((group) => (
          <Box key={group.id} sx={{ mb: 2, p: 1.5, border: "1px solid #ddd", borderRadius: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} gap={1} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Within group</InputLabel>
                <Select
                  label="Within group"
                  value={group.logic}
                  onChange={(e) => updateGroupLogic(group.id, e.target.value as GroupLogic)}
                >
                  <MenuItem value="AND">AND</MenuItem>
                  <MenuItem value="OR">OR</MenuItem>
                </Select>
              </FormControl>

              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  size="small"
                  startIcon={<Add />}
                  variant="outlined"
                  onClick={() => addRowToGroup(group.id)}
                  sx={{ textTransform: "none" }}
                >
                  Add condition
                </Button>

                {draft.groups.length > 1 && (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => removeGroup(group.id)}
                    sx={{ textTransform: "none" }}
                  >
                    Remove group
                  </Button>
                )}
              </Box>
            </Box>

            <Grid container spacing={2}>
              {group.rows.map((row) => {
                const selectedParam = row.code ? getParamByCode(row.code) : undefined;

                return (
                  <Grid item xs={12} key={row.id}>
                    <Box sx={{ border: "1px solid #eee", borderRadius: 2, p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        {/* Param picker */}
                        <Grid item xs={12} md={4}>
                          <Autocomplete
                            size="small"
                            options={params}
                            getOptionLabel={(p) => p.label}
                            value={selectedParam ?? null}
                            onChange={(_, p) => {
                              if (!p) {
                                updateRow(group.id, row.id, {
                                  code: "",
                                  label: "",
                                  type: 1,
                                  op: "eq",
                                  v1: null,
                                  v2: null,
                                } as any);
                                return;
                              }

                              if (p.type === 2) {
                                updateRow(group.id, row.id, {
                                  code: p.code,
                                  label: p.label,
                                  type: 2,
                                  op: "eq",
                                  v1: null,
                                } as any);
                              } else {
                                updateRow(group.id, row.id, {
                                  code: p.code,
                                  label: p.label,
                                  type: (p.type as 1 | 3) ?? 1,
                                  op: "eq",
                                  v1: null,
                                  v2: null,
                                } as any);
                              }
                            }}
                            renderInput={(pp) => <TextField {...pp} size="small" label="Parameter" />}
                          />
                        </Grid>

                        {/* Operator */}
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Op</InputLabel>
                            <Select
                              label="Op"
                              value={row.op}
                              onChange={(e) => {
                                const op = e.target.value as any;

                                if (row.type === 2) {
                                  updateRow(group.id, row.id, { op, v1: op === "in" ? [] : null } as any);
                                } else {
                                  updateRow(group.id, row.id, { op, v1: null, v2: null } as any);
                                }
                              }}
                              disabled={!row.code}
                            >
                              {(row.type === 2 ? selectOps : numOps).map((o) => (
                                <MenuItem key={o.key} value={o.key}>
                                  {o.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Value(s) */}
                        <Grid item xs={12} md={5}>
                          {!row.code ? (
                            <TextField size="small" fullWidth disabled label="Value" />
                          ) : row.type === 2 ? (
                            <FormControl fullWidth size="small">
                              <InputLabel>Value</InputLabel>
                              <Select
                                label="Value"
                                value={row.v1 ?? (row.op === "in" ? [] : "")}
                                multiple={row.op === "in"}
                                onChange={(e) => updateRow(group.id, row.id, { v1: e.target.value as any } as any)}
                                renderValue={(selected) => {
                                  if (!selectedParam) return String(selected ?? "");
                                  if (Array.isArray(selected)) {
                                    return selected.map((v) => optionLabel(selectedParam, String(v))).join(", ");
                                  }
                                  return optionLabel(selectedParam, String(selected));
                                }}
                              >
                                {(selectedParam?.options ?? []).map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value} disabled={opt.status === false}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : row.op === "between" ? (
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="From"
                                  type="number"
                                  value={row.v1 ?? ""}
                                  onChange={(e) =>
                                    updateRow(group.id, row.id, {
                                      v1: e.target.value === "" ? null : Number(e.target.value),
                                    } as any)
                                  }
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="To"
                                  type="number"
                                  value={row.v2 ?? ""}
                                  onChange={(e) =>
                                    updateRow(group.id, row.id, {
                                      v2: e.target.value === "" ? null : Number(e.target.value),
                                    } as any)
                                  }
                                />
                              </Grid>
                            </Grid>
                          ) : (
                            <TextField
                              size="small"
                              fullWidth
                              label="Value"
                              type="number"
                              value={row.v1 ?? ""}
                              onChange={(e) =>
                                updateRow(group.id, row.id, {
                                  v1: e.target.value === "" ? null : Number(e.target.value),
                                } as any)
                              }
                            />
                          )}
                        </Grid>

                        {/* Remove row */}
                        <Grid item xs={12} md={1} display="flex" justifyContent="flex-end">
                          <IconButton onClick={() => removeRow(group.id, row.id)} aria-label="remove filter">
                            <DeleteOutline color="error"/>
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="error" onClick={resetAll}>
          Reset
        </Button>
        <Button variant="outlined" color="error" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="outlined"
          disabled={!canApply}
          onClick={() => {
            onApply(draft);
            onClose();
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}



function ExprToken({
  children,
  kind,
}: {
  children: React.ReactNode;
  kind: "paren" | "logic" | "cond";
}) {
  const sx =
    kind === "paren"
      ? { px: 1, py: 0.25, borderRadius: 999, bgcolor: "#eee", fontSize: 12 }
      : kind === "logic"
        ? { px: 1, py: 0.25,  borderRadius: 999, bgcolor: "#dfefff", fontSize: 12, fontWeight: 700 }
        : { px: 1, py: 0.25, color: 'purple', borderRadius: 999, bgcolor: "#f6f6f6", fontSize: 13 };

  return <Box component="span" sx={sx}>{children}</Box>;
}

export function FilterExpressionPreview({
  filter,
  getParamByCode,
  summarizeRow,
  rowIsComplete,
}: {
  filter: ParamFilterState;
  getParamByCode: (code: string) => Parameter | undefined;
  summarizeRow: (row: FilterRow, getParamByCode: (code: string) => Parameter | undefined) => string;
  rowIsComplete: (row: FilterRow) => boolean;
}) {
  const activeGroups = (filter.groups ?? [])
    .map((g) => ({
      ...g,
      rows: (g.rows ?? []).filter((r) => r.code && rowIsComplete(r)),
    }))
    .filter((g) => g.rows.length > 0);

  if (activeGroups.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No filters applied. Add conditions to build an expression.
        </Typography>
        <Divider sx={{ my: 1 }} />
        <div className="flex items-center gap-1">
          <TipsAndUpdatesOutlined color="warning" sx={{ cursor: 'pointer' }} />
          <Typography
            variant="caption"
            className="text-gray-500"
          >
            Use a new group to create parentheses logic. Example:
            A AND (B OR C) → use 2 groups, first AND group, second OR group.
          </Typography>
        </div>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, mb: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Preview
      </Typography>

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mt={1}>
        {activeGroups.map((g, gi) => (
          <React.Fragment key={g.id}>
            {gi > 0 && <ExprToken kind="logic">{filter.logic}</ExprToken>}

            <ExprToken kind="paren">(</ExprToken>

            {g.rows.map((r, ri) => (
              <React.Fragment key={r.id}>
                {ri > 0 && <ExprToken kind="logic">{g.logic}</ExprToken>}
                <ExprToken kind="cond">{summarizeRow(r, getParamByCode)}</ExprToken>
              </React.Fragment>
            ))}

            <ExprToken kind="paren">)</ExprToken>
          </React.Fragment>
        ))}
      </Box>

      <Divider sx={{ my: 1 }} />

      <div className="flex items-center gap-1">
        <TipsAndUpdatesOutlined color="warning" sx={{ cursor: 'pointer' }} />
        <Typography
          variant="caption"
          className="text-gray-500"
        >
          Use a new group to create parentheses logic. Example:
          A AND (B OR C) → use 2 groups, first AND group, second OR group.
        </Typography>
      </div>
    </Paper>
  );
}